// src/common/config/env.js
// Configuration for application environments: DEMO, DEVELOPMENT, PRODUCTION

import { useState, useEffect } from 'react';

/**
 * Get the current application mode.
 * Reads from NEXT_PUBLIC_APP_MODE (client-side) or NODE_ENV (server-side fallback).
 * Valid modes: 'demo', 'development', 'production'. Defaults to 'production'.
 * @returns {string} The current mode.
 */
export const getEnvMode = () => {
  const clientMode = typeof window !== 'undefined' ? process.env.REACT_APP_APP_MODE : undefined;
  const mode = clientMode || process.env.NODE_ENV || 'production';
  console.log('[DEBUG ENV] Client mode from REACT_APP_APP_MODE:', clientMode, 'Final mode:', mode);
  
  const validModes = ['demo', 'development', 'production'];
  return validModes.includes(mode) ? mode : 'production';
};

/**
 * Check if the current mode is demo.
 * @returns {boolean} True if in demo mode.
 */
export const isDemoMode = () => {
  const mode = getEnvMode();
  console.log('[DEBUG ENV] isDemoMode check, current mode:', mode, 'Is demo:', mode === 'demo');
  return mode === 'demo';
};

/**
 * Check if the current mode is development.
 * @returns {boolean} True if in development mode.
 */
export const isDevelopmentMode = () => getEnvMode() === 'development';

/**
 * Check if the current mode is production.
 * @returns {boolean} True if in production mode.
 */
export const isProductionMode = () => getEnvMode() === 'production';

/**
 * Get the API base URL based on environment.
 * In demo mode, uses mock endpoints; otherwise, real API.
 * @returns {string} The API base URL.
 */
export const getApiBaseUrl = () => {
  if (isDemoMode()) {
    return '/api/mock'; // Mock API routes will be created later
  }
  if (isDevelopmentMode()) {
    return process.env.NEXT_PUBLIC_API_URL_DEV || 'http://localhost:3001/api';
  }
  return process.env.NEXT_PUBLIC_API_URL_PROD || 'https://api.milytravel.com';
};

/**
 * Hook to get environment mode in React components.
 * Useful for client-side rendering.
 */
export const useEnvMode = () => {
  const [mode, setMode] = useState(getEnvMode());
  
  useEffect(() => {
    // Listen for mode changes (e.g., via localStorage for demo switching)
    const handleStorageChange = () => {
      setMode(getEnvMode());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  return mode;
};